import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, CalendarCheck, FileText, MessageSquare, CheckCircle2, Loader2, ChevronLeft, ChevronRight,
} from "lucide-react";
import { api, resolveImageUrl, optimizedImageUrl } from "@/lib/api";
import { fmtMoney, fmtKm } from "@/lib/format";
import { useSite, usePageSeo } from "@/context/SiteContext";
import { useJsonLd } from "@/lib/jsonld";
import { Badge } from "@/components/ui/badge";
import { FinancingCalculator } from "@/components/FinancingCalculator";
import { LeadCaptureModal } from "@/components/LeadCaptureModal";
import { VehicleCard } from "@/components/VehicleCard";

export default function VehicleDetailPage() {
  const { id } = useParams();
  const { categoryTitle, publicUrl } = useSite();
  const [vehicle, setVehicle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [modal, setModal] = useState(null);

  const isSold = vehicle?.status === "sold";
  const ymm = vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? ` ${vehicle.trim}` : ""}` : "";

  usePageSeo(
    vehicle ? (vehicle.seo_title || (isSold ? `${ymm} - Sold` : `${ymm} for Sale in Edmonton, AB`)) : undefined,
    vehicle?.seo_description || vehicle?.description?.slice(0, 155),
    vehicle?.images?.[0] ? resolveImageUrl(vehicle.images[0]) : undefined
  );

  useJsonLd(
    vehicle
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: publicUrl || "/" },
                { "@type": "ListItem", position: 2, name: "Inventory", item: `${publicUrl}/inventory` },
                { "@type": "ListItem", position: 3, name: ymm, item: `${publicUrl}/vehicles/${vehicle.id}` },
              ],
            },
            {
              "@type": "Vehicle",
              name: vehicle.title,
              brand: { "@type": "Brand", name: vehicle.make },
              model: vehicle.model,
              vehicleModelDate: String(vehicle.year),
              ...(vehicle.vin && { vehicleIdentificationNumber: vehicle.vin }),
              mileageFromOdometer: { "@type": "QuantitativeValue", value: vehicle.mileage, unitCode: "KMT" },
              ...(vehicle.fuel_type && { fuelType: vehicle.fuel_type }),
              ...(vehicle.transmission && { vehicleTransmission: vehicle.transmission }),
              ...(vehicle.drivetrain && { driveWheelConfiguration: vehicle.drivetrain }),
              ...(vehicle.exterior_color && { color: vehicle.exterior_color }),
              ...(vehicle.images?.length && { image: vehicle.images.map((i) => resolveImageUrl(i)) }),
              ...(publicUrl && { url: `${publicUrl}/vehicles/${vehicle.id}` }),
              offers: {
                "@type": "Offer",
                price: vehicle.sale_price || vehicle.price,
                priceCurrency: "CAD",
                availability:
                  vehicle.status === "available"
                    ? "https://schema.org/InStock"
                    : vehicle.status === "sold"
                      ? "https://schema.org/SoldOut"
                      : "https://schema.org/LimitedAvailability",
              },
            },
          ],
        }
      : null
  );

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setActiveImg(0);
    api
      .get(`/vehicles/${id}`)
      .then((r) => {
        setVehicle(r.data);
        api
          .get("/vehicles", { params: { category: r.data.category } })
          .then((rel) => setRelated(rel.data.filter((v) => v.id !== r.data.id).slice(0, 3)))
          .catch(() => {});
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="vehicle-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound || !vehicle) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4" data-testid="vehicle-not-found">
        <p className="font-display text-2xl font-bold uppercase text-foreground">Vehicle Not Found</p>
        <p className="text-sm text-muted-foreground">This vehicle may have been sold or removed.</p>
        <Link to="/inventory" className="btn-themed" data-testid="vehicle-back-to-inventory">
          Browse Available Inventory
        </Link>
      </div>
    );
  }

  const onSale = !isSold && vehicle.sale_price && vehicle.sale_price < vehicle.price;
  const specs = [
    ["VIN", vehicle.vin], ["Stock #", vehicle.stock_number], ["Engine", vehicle.engine],
    ["Engine Type", vehicle.engine_type], ["Transmission", vehicle.transmission],
    ["Drivetrain", vehicle.drivetrain], ["Towing Capacity", vehicle.towing_capacity],
    ["Payload", vehicle.payload], ["Horsepower", vehicle.horsepower], ["Torque", vehicle.torque],
    ["Truck Class", vehicle.truck_class], ["Fuel Type", vehicle.fuel_type],
    ["Exterior", vehicle.exterior_color], ["Interior", vehicle.interior_color],
    ["Condition", vehicle.condition], ["Category", categoryTitle(vehicle.category)],
  ].filter(([, v]) => v);

  const galleryNav = (dir) => {
    const n = vehicle.images?.length || 0;
    if (n < 2) return;
    setActiveImg((i) => (i + dir + n) % n);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="vehicle-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav aria-label="Breadcrumb" className="mb-6 text-sm">
          <ol className="flex flex-wrap items-center gap-2 text-muted-foreground">
            <li><Link to="/" className="hover:text-primary">Home</Link></li>
            <li aria-hidden="true">/</li>
            <li><Link to="/inventory" className="hover:text-primary">Inventory</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-foreground font-medium" aria-current="page">{ymm}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <div
              className="relative aspect-[16/10] bg-muted overflow-hidden border border-border rounded-md mb-3 outline-none focus-visible:ring-2 focus-visible:ring-primary"
              tabIndex={0}
              role="region"
              aria-label="Vehicle image gallery. Use left and right arrow keys to navigate."
              onKeyDown={(e) => {
                if (e.key === "ArrowLeft") galleryNav(-1);
                if (e.key === "ArrowRight") galleryNav(1);
              }}
              data-testid="vehicle-gallery-main"
            >
              {vehicle.images?.length ? (
                <img src={optimizedImageUrl(resolveImageUrl(vehicle.images[activeImg]), 1400)} alt={`${ymm} - photo ${activeImg + 1}`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground font-mono uppercase text-sm">
                  Photo Coming Soon
                </div>
              )}
              {isSold && (
                <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
                  <span className="bg-white/95 text-foreground font-display font-black uppercase tracking-widest px-5 py-2 rounded-sm" data-testid="vehicle-sold-overlay">
                    Sold
                  </span>
                </div>
              )}
              {!isSold && vehicle.images?.length > 1 && (
                <>
                  <button
                    onClick={() => galleryNav(-1)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black/90 text-white p-2 rounded-full"
                    aria-label="Previous photo"
                    data-testid="gallery-prev"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => galleryNav(1)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black/90 text-white p-2 rounded-full"
                    aria-label="Next photo"
                    data-testid="gallery-next"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
            {vehicle.images?.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-1" data-testid="vehicle-gallery-thumbs">
                {vehicle.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`shrink-0 w-24 aspect-video overflow-hidden rounded-sm border-2 transition-colors ${
                      i === activeImg ? "border-primary" : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                    aria-label={`View photo ${i + 1}`}
                    data-testid={`vehicle-thumb-${i}`}
                  >
                    <img src={optimizedImageUrl(resolveImageUrl(img), 300)} alt={`${ymm} thumbnail ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}

            <div className="bg-card border border-border rounded-md p-6 mt-6">
              <h2 className="font-display text-xl font-bold uppercase text-foreground mb-4">Specifications</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8" data-testid="vehicle-specs-grid">
                {specs.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-2.5 border-b border-border text-sm">
                    <span className="text-muted-foreground font-mono uppercase text-xs tracking-wider">{k}</span>
                    <span className="text-foreground text-right font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {vehicle.features?.length > 0 && (
              <div className="bg-card border border-border rounded-md p-6 mt-6">
                <h2 className="font-display text-xl font-bold uppercase text-foreground mb-4">Features</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="vehicle-features-list">
                  {vehicle.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm text-foreground">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {vehicle.description && (
              <div className="bg-card border border-border rounded-md p-6 mt-6">
                <h2 className="font-display text-xl font-bold uppercase text-foreground mb-3">About This Vehicle</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line" data-testid="vehicle-description">{vehicle.description}</p>
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card border border-border rounded-md p-6">
              <div className="flex flex-wrap gap-2 mb-4">
                {vehicle.condition && (
                  <Badge className="bg-primary/10 border border-primary/30 text-primary uppercase text-[10px] tracking-wider rounded-sm">
                    {vehicle.condition}
                  </Badge>
                )}
                <Badge variant="outline" className="border-border text-muted-foreground uppercase text-[10px] tracking-wider rounded-sm">
                  {categoryTitle(vehicle.category)}
                </Badge>
                {vehicle.status === "pending" && (
                  <Badge className="bg-amber-500 text-black border-0 uppercase text-[10px] tracking-wider rounded-sm" data-testid="vehicle-status-badge">
                    Pending Sale
                  </Badge>
                )}
                {isSold && (
                  <Badge className="bg-foreground text-background border-0 uppercase text-[10px] tracking-wider rounded-sm" data-testid="vehicle-status-badge">
                    Sold
                  </Badge>
                )}
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight text-foreground leading-tight mb-2" data-testid="vehicle-title">
                {ymm}
              </h1>
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-5">
                {fmtKm(vehicle.mileage)}
              </p>

              {isSold ? (
                <div data-testid="vehicle-sold-panel">
                  <p className="font-display text-2xl font-black uppercase text-muted-foreground mb-2">This Vehicle Has Been Sold</p>
                  <p className="text-sm text-muted-foreground mb-6">This vehicle has found a new owner. Browse our current inventory or contact us to find something similar.</p>
                  <div className="space-y-3">
                    <Link to="/inventory" className="btn-themed w-full" data-testid="sold-browse-inventory">
                      Browse Available Inventory
                    </Link>
                    <Link to="/contact" className="btn-themed-outline w-full" data-testid="sold-find-similar">
                      Contact Us to Find Something Similar
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-baseline gap-3 mb-6">
                    {onSale ? (
                      <>
                        <p className="font-display text-4xl font-black text-primary" data-testid="vehicle-price">{fmtMoney(vehicle.sale_price)}</p>
                        <p className="text-lg text-muted-foreground line-through">{fmtMoney(vehicle.price)}</p>
                      </>
                    ) : (
                      <p className="font-display text-4xl font-black text-primary" data-testid="vehicle-price">{fmtMoney(vehicle.price)}</p>
                    )}
                    <span className="text-xs text-muted-foreground">CAD</span>
                  </div>
                  <div className="space-y-3">
                    <button onClick={() => setModal("general_inquiry")} className="btn-themed w-full" data-testid="vehicle-inquiry-btn">
                      <MessageSquare className="h-4 w-4" /> I'm Interested
                    </button>
                    <button onClick={() => setModal("test_drive")} className="btn-themed-outline w-full" data-testid="vehicle-test-drive-btn">
                      <CalendarCheck className="h-4 w-4" /> Book a Test Drive
                    </button>
                    <button onClick={() => setModal("quote")} className="btn-themed-outline w-full" data-testid="vehicle-quote-btn">
                      <FileText className="h-4 w-4" /> Get a Quote
                    </button>
                  </div>
                </>
              )}
            </div>

            {!isSold && <FinancingCalculator defaultPrice={onSale ? vehicle.sale_price : vehicle.price} />}
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-16" data-testid="related-vehicles">
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-foreground mb-6">
              {isSold ? "Available" : "Similar"} {categoryTitle(vehicle.category)}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((v) => (
                <VehicleCard key={v.id} vehicle={v} />
              ))}
            </div>
          </div>
        )}
      </div>

      <LeadCaptureModal
        open={modal !== null}
        onOpenChange={(o) => !o && setModal(null)}
        type={modal || "general_inquiry"}
        vehicle={vehicle}
      />
    </div>
  );
}
